import React, { useState } from "react";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import { AppSidebar } from "@/components/dashboard/SimpleSidebar";
import { Menu, X } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import Link from "next/link";
import { Button } from "@/components/ui/Button";
import useCurrentAccount from "@/components/context/useCurrentAccount";
import { getSubscriptionState, isSubscriptionBlocked } from "@/utils/billing";
import { SuspendedBanner } from "@/components/SuspendedBanner";

interface DashboardLayoutProps {
  activePath: string;
  children: React.ReactNode;
  title?: string;
  noindex?: boolean;
  nofollow?: boolean;
  breadcrumbs?: Array<{
    label: string;
    href?: string;
  }>;
}

export default function DashboardLayout({
  activePath,
  children,
  title,
  noindex = true,
  nofollow = true,
  breadcrumbs,
}: DashboardLayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { account } = useCurrentAccount();
  const subscriptionState = getSubscriptionState(account);

  return (
    <>
      {title && <Head>{generateNextSeo({ title, noindex, nofollow })}</Head>}
      <div className="flex min-h-screen w-full">
        {/* Desktop Sidebar */}
        <AppSidebar activePath={activePath} />

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
            <AppSidebar
              activePath={activePath}
              className="fixed left-0 top-0 bottom-0 w-64 flex bg-sidebar-background"
            />
          </div>
        )}

        <div className="flex-1 flex flex-col">
          {isSubscriptionBlocked(account) && <SuspendedBanner />}
          {subscriptionState.status === "trialing" && !account?.current_payment_method && (
            <div className="bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200 px-4 py-2 text-sm text-center">
              Your free trial ends in {subscriptionState.daysRemaining} day
              {subscriptionState.daysRemaining !== 1 ? "s" : ""}.{" "}
              <Link href="/dashboard/billing" className="underline font-medium">
                Subscribe now
              </Link>{" "}
              to keep your monitors running.
            </div>
          )}
          <header className="flex h-16 items-center gap-2 border-b bg-background px-4 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
            {breadcrumbs && breadcrumbs.length > 0 && (
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link href="/dashboard">Dashboard</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={index}>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        {crumb.href ? (
                          <BreadcrumbLink asChild>
                            <Link href={crumb.href}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        ) : (
                          <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                        )}
                      </BreadcrumbItem>
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            )}
          </header>
          <main className="flex-1">{children}</main>
        </div>
      </div>
    </>
  );
}
